"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, Sparkles, CircleCheck as CheckCircle2, ArrowRight, MessageSquare } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "faq-1",
  question: "Can I really do SEO myself without technical expertise?",
  answer: "Yes, absolutely. Essential search engine optimisation — including keyword research, on-page content optimisation, meta updates, and basic audits — is entirely accessible without technical expertise when using structured DIY software. If you encounter intricate technical hurdles such as complex server configurations, indexation anomalies, or large-scale site migrations, you can bring me in to handle the specialist technical work while you manage day-to-day improvements."
}, {
  id: "faq-2",
  question: "How long does it take to see results with DIY SEO software?",
  answer: "In low-competition niches, you can often see modest organic traffic gains within 3 to 4 months of consistent implementation. For most businesses, measurable ranking and traffic improvements typically emerge between 6 to 12 months, whilst highly competitive sectors may take 12 to 18 months. Ultimately, your timeline depends on consistency, execution speed, and the editorial quality of the content you publish."
}, {
  id: "faq-3",
  question: "What's the difference between free and paid SEO tools?",
  answer: "Free tools such as Google Search Console and Bing Webmaster Tools provide vital baseline data, including crawl errors, search impressions, and basic indexing status. Paid SEO platforms add deeper competitor intelligence, historical backlink analysis, frequent rank tracking, automated site audits, and AI-assisted content recommendations that help you prioritise high-impact actions quickly."
}, {
  id: "faq-4",
  question: "When should I consider hiring an SEO consultant instead?",
  answer: "I recommend hiring an independent consultant when you lack 5 to 10 focused hours monthly, operate in an intensely competitive market, have serious technical debt, or need a bespoke strategic roadmap. Many of my clients start with DIY SEO software to build foundational momentum, then partner directly with me for what software alone cannot resolve."
}, {
  id: "faq-5",
  question: "How do I choose the right SEO software for my business?",
  answer: "Start by identifying your primary requirement first — whether that is discovering keyword opportunities, conducting technical audits, or optimising on-page copy. I advise choosing one focused, user-friendly tool that solves your immediate priority rather than committing to a complex all-in-one suite before you know exactly which features your workflow requires."
}];
export default function Faq() {
  const shouldReduceMotion = useReducedMotion();
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqList.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
  return <section id="faqaccordion" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase mb-4 shadow-xs">
            <Sparkles className="size-3.5 text-accent" />
            <span>AI-Optimized Knowledge Base</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold font-default text-foreground tracking-tight mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto font-sans leading-relaxed">
            Clear, authoritative answers regarding DIY SEO software, implementation timelines, and when to partner directly with Yassine Essaadi for bespoke strategic growth.
          </p>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: "easeOut"
      }} className="mb-12">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqList.map((item, index) => <div data-index={index} key={item.id}>
                <AccordionItem value={item.id} className="bg-card border border-border rounded-xl shadow-xs overflow-hidden transition-all duration-200 hover:border-primary/40">
                  <AccordionTrigger className="bg-primary text-primary-foreground px-6 py-5 hover:no-underline font-semibold text-base md:text-lg text-left transition-colors flex justify-between items-center gap-4 [&[data-state=open]]:bg-primary/95 [&_svg]:text-accent [&_svg]:bg-background/10 [&_svg]:p-1 [&_svg]:rounded-full [&_svg]:size-6">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="size-5 shrink-0 text-accent" />
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="bg-card text-card-foreground px-6 pt-5 pb-6 text-base font-sans leading-relaxed border-t border-border/50">
                    <p className="text-foreground/90">{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-muted/80 border border-border rounded-2xl p-6 md:p-8 mb-12 shadow-xs">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-lg bg-accent/15 text-accent shrink-0 mt-0.5 md:mt-0">
                <CheckCircle2 className="size-6" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground font-default mb-1">
                  Why Direct Consultant Guidance Matters
                </h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                  Search engines and AI overview engines prioritize natural authority and verified expert insights. Working directly with me ensures your site strategy aligns with current search standards.
                </p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0 mt-2 md:mt-0">
              <Link to="/ContactPage">
                <span>Ask Yassine a Question</span>
                <MessageSquare className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.25,
        ease: "easeOut"
      }} className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-10 text-center shadow-md relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold font-default tracking-tight">
              Ready to Upgrade Your SEO Performance?
            </h3>
            <p className="text-primary-foreground/90 text-sm md:text-base font-sans leading-relaxed">
              Deciding between DIY tools and specialist guidance? Let's review your site's current position and determine whether self-serve software or dedicated consultant support is right for your goals.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-medium shadow-xs">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  <span>Explore Case Studies</span>
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}